import React, { useState } from 'react';
import {
  Box,
  Typography,
  TextField,
  Button,
  Paper,
  Grid,
  Card,
  CardContent,
  Alert,
  CircularProgress,
  Avatar,
} from '@mui/material';
import {
  Business as BusinessIcon,
  Email as EmailIcon,
  Phone as PhoneIcon,
  Language as LanguageIcon,
  LocationOn as LocationIcon,
  Save as SaveIcon,
} from '@mui/icons-material';
import EditProfileButton from './EditProfileButton';
import { BRANDING } from '../../../utils/branding';

const OrganizationProfile = () => {
  const [isEditing, setIsEditing] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errors, setErrors] = useState({});

  // Organization profile data
  const [profile, setProfile] = useState({
    organizationName: BRANDING.name,
      email: 'info@a1.com',
      phone: '+91-9876543210',
      website: 'https://www.a1.com',
    address: '123 Tech Park, Electronic City',
      city: 'Bangalore',
      state: 'Karnataka',
      country: 'India',
      postalCode: '560100',
    registrationNumber: 'CIN123456789',
    taxId: 'TAX987654321',
    description: 'Leading technology company specializing in software development and IT solutions.',
  });

  // Handle input changes
  const handleInputChange = (field, value) => {
    setProfile(prev => ({
      ...prev,
        [field]: value,
    }));
    // Clear error for this field
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: '' }));
    }
  };

  // Validate form
  const validateForm = () => {
    const newErrors = {};
    if (!profile.organizationName) newErrors.organizationName = 'Organization name is required';
    if (!profile.email) newErrors.email = 'Email is required';
    if (!profile.phone) newErrors.phone = 'Phone is required';
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Handle save
  const handleSave = async () => {
    if (!validateForm()) {
      return;
    }

    setIsLoading(true);
    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 1500));
      
      setSuccessMessage('Organization profile updated successfully!');
      setIsEditing(false);
      setTimeout(() => setSuccessMessage(''), 3000);
    } catch (error) {
      setErrors({ submit: 'Failed to update profile. Please try again.' });
    } finally {
      setIsLoading(false);
    }
  };

  // Handle edit button click
  const handleEdit = () => {
    setIsEditing(true);
    setErrors({});
  };

  // Handle cancel
  const handleCancel = () => {
    setIsEditing(false);
    setErrors({});
    // Reset to original values if needed
  };

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <BusinessIcon sx={{ fontSize: 40, color: 'primary.main' }} />
          <Box>
            <Typography variant="h4" sx={{ fontWeight: 'bold', color: '#2c3e50' }}>
              Organization Profile
            </Typography>
            <Typography variant="body2" color="text.secondary">
              View and manage organization information
            </Typography>
          </Box>
        </Box>
        {!isEditing && <EditProfileButton onClick={handleEdit} />}
      </Box>

      {/* Success Message */}
      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }}>
          {successMessage}
        </Alert>
      )}

      {/* Error Message */}
      {errors.submit && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {errors.submit}
        </Alert>
      )}

      {/* Profile Content */}
      <Grid container spacing={3}>
        {/* Organization Logo & Basic Info Card */}
        <Grid item xs={12} md={4}>
          <Card sx={{ height: '100%' }}>
            <CardContent sx={{ textAlign: 'center' }}>
              <Avatar
                sx={{
                  width: 120,
                  height: 120,
                  margin: '0 auto 16px',
                  backgroundColor: 'primary.main',
                  fontSize: 48,
                }}
              >
                {profile.organizationName.charAt(0)}
                  </Avatar>
              <Typography variant="h5" sx={{ fontWeight: 'bold', mb: 1 }}>
                {profile.organizationName}
              </Typography>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                {profile.description}
              </Typography>
            </CardContent>
          </Card>
        </Grid>

        {/* Profile Details Form */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Grid container spacing={2}>
              {/* Organization Name */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Organization Name *"
                  value={profile.organizationName}
                  onChange={(e) => handleInputChange('organizationName', e.target.value)}
                  disabled={!isEditing}
                  error={Boolean(errors.organizationName)}
                  helperText={errors.organizationName}
                  InputProps={{
                    startAdornment: <BusinessIcon sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                />
              </Grid>

              {/* Email & Phone */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Email *"
                    type="email"
                  value={profile.email}
                  onChange={(e) => handleInputChange('email', e.target.value)}
                    disabled={!isEditing}
                  error={Boolean(errors.email)}
                  helperText={errors.email}
                  InputProps={{
                    startAdornment: <EmailIcon sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Phone *"
                  value={profile.phone}
                  onChange={(e) => handleInputChange('phone', e.target.value)}
                    disabled={!isEditing}
                  error={Boolean(errors.phone)}
                  helperText={errors.phone}
                  InputProps={{
                    startAdornment: <PhoneIcon sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                />
              </Grid>

              {/* Website */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Website"
                  value={profile.website}
                  onChange={(e) => handleInputChange('website', e.target.value)}
                    disabled={!isEditing}
                  InputProps={{
                    startAdornment: <LanguageIcon sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                />
              </Grid>

              {/* Address */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Address"
                  value={profile.address}
                  onChange={(e) => handleInputChange('address', e.target.value)}
                    disabled={!isEditing}
                  InputProps={{
                    startAdornment: <LocationIcon sx={{ mr: 1, color: 'action.active' }} />,
                  }}
                />
              </Grid>

              {/* City, State, Country, Postal Code */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="City"
                  value={profile.city}
                  onChange={(e) => handleInputChange('city', e.target.value)}
                    disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="State"
                  value={profile.state}
                  onChange={(e) => handleInputChange('state', e.target.value)}
                      disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Country"
                  value={profile.country}
                  onChange={(e) => handleInputChange('country', e.target.value)}
                      disabled={!isEditing}
                />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Postal Code"
                  value={profile.postalCode}
                  onChange={(e) => handleInputChange('postalCode', e.target.value)}
                      disabled={!isEditing}
                />
              </Grid>

              {/* Registration Number & Tax ID */}
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Registration Number"
                  value={profile.registrationNumber}
                  onChange={(e) => handleInputChange('registrationNumber', e.target.value)}
                      disabled={!isEditing}
                    />
              </Grid>
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Tax ID"
                  value={profile.taxId}
                  onChange={(e) => handleInputChange('taxId', e.target.value)}
                      disabled={!isEditing}
                    />
              </Grid>

              {/* Description */}
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  label="Description"
                  multiline
                  rows={3}
                  value={profile.description}
                  onChange={(e) => handleInputChange('description', e.target.value)}
                      disabled={!isEditing}
                />
              </Grid>

              {/* Action Buttons (only shown when editing) */}
              {isEditing && (
                <Grid item xs={12}>
                  <Box sx={{ display: 'flex', gap: 2, justifyContent: 'flex-end' }}>
                    <Button
                      variant="outlined"
                      onClick={handleCancel}
                      disabled={isLoading}
                    >
                      Cancel
                    </Button>
                    <Button
                      variant="contained"
                      startIcon={isLoading ? <CircularProgress size={20} /> : <SaveIcon />}
                      onClick={handleSave}
                      disabled={isLoading}
                    >
                      {isLoading ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </Box>
                </Grid>
              )}
            </Grid>
          </Paper>
        </Grid>
      </Grid>
    </Box>
  );
};

export default OrganizationProfile;
